import { defineComponent, reactive, ref, computed, toRef, watch, nextTick, resolveComponent, openBlock, createElementBlock, normalizeClass, createElementVNode, createVNode, withCtx, withDirectives, mergeProps, vModelDynamic, createBlock, withModifiers, createCommentVNode, createSlots, renderSlot, createTextVNode, toDisplayString, vShow } from 'vue';
import { K, _, c as ie, r as re } from './OrAutocompleteV3-8c6de221.js';
import { InlineInputBoxSize, OrInlineInputBoxV3 as script$5 } from './or-inline-input-box-v3/index.js';
import { OrErrorV3 as script$1 } from './or-error-v3/index.js';
import { OrTextV3 as script$2 } from './or-text-v3/index.js';
import { OrHintV3 as script$3 } from './or-hint-v3/index.js';
import { OrIconButtonV3 as script$4 } from './or-icon-button-v3/index.js';

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

const InlineInput = [
// Layout
'layout-column',
// Spacing
'gap-xs'];
const InlineInputControl = [
// Box
'w-full',
// Typography
'typography-inherit',
// Theme (placeholder)
'placeholder:theme-foreground-outline', 'dark:placeholder:theme-foreground-outline-dark'];
const InlineInputAction = [
// Layout
'layout-inline-row self-start',
// Spacing
'gap-sm',
// Theme
'theme-foreground-outline', 'dark:theme-foreground-outline-dark'];
const InlineInputActionSizes = {
  [InlineInputBoxSize.L]: [
  // Spacing
  'px-0', 'py-sm'],
  [InlineInputBoxSize.M]: [
  // Spacing
  'px-0', 'py-xs'],
  [InlineInputBoxSize.S]: [
  // Spacing
  'px-0', 'py-xs*']
};

var InlineInputType;
(function (InlineInputType) {
  InlineInputType["Text"] = "text";
  InlineInputType["Number"] = "number";
})(InlineInputType || (InlineInputType = {}));

var script = defineComponent({
  name: 'OrInlineInput',
  components: {
    OrError: script$1,
    OrText: script$2,
    OrHint: script$3,
    OrIconButton: script$4,
    OrInlineInputBox: script$5
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    modelValue: {
      type: undefined,
      default: undefined
    },
    type: {
      type: String,
      default: () => InlineInputType.Text
    },
    size: {
      type: String,
      default: () => InlineInputBoxSize.M
    },
    placeholder: {
      type: String,
      default: undefined
    },
    hint: {
      type: String,
      default: undefined
    },
    error: {
      type: [String, Boolean],
      default: undefined
    },
    saveOnBlur: {
      type: Boolean,
      default: false
    },
    sliceIndex: {
      type: Number,
      default: 0
    }
  },
  emits: ['update:modelValue', 'input', 'keydown', 'keyup', 'focus', 'blur', 'apply', 'reset'],
  expose: ['root', 'apply', 'reset', 'focus', 'blur'],
  setup(props, context) {
    const attributes = reactive({
      id: K(),
      ..._(),
      ...ie()
    });
    // Refs & Styles
    const root = ref();
    const rootStyles = computed(() => ['or-inline-input-v3', ...InlineInput]);
    const control = ref();
    const controlStyles = computed(() => [...InlineInputControl]);
    const actionStyles = computed(() => [...InlineInputAction, ...InlineInputActionSizes[props.size]]);
    // State
    const model = re(toRef(props, 'modelValue'), context.emit);
    const draft = ref(model.value);
    watch(model, value => {
      draft.value = value;
    });
    const activated = ref(false);
    const length = computed(() => {
      var _a, _b;
      return (_b = (_a = draft.value) === null || _a === void 0 ? void 0 : _a.toString().length) !== null && _b !== void 0 ? _b : 0;
    });
    const maxLength = computed(() => {
      return attributes.maxlength;
    });
    // Handlers
    function onFocus() {
      activated.value = true;
    }
    function onTextClick() {
      if (!activated.value) {
        activated.value = true;
      }
      nextTick(() => focus());
    }
    function onBlur() {
      if (props.saveOnBlur) {
        apply();
      }
    }
    // Methods
    function apply() {
      if (draft.value !== model.value) {
        model.value = draft.value;
      }
      activated.value = false;
      nextTick(() => context.emit('apply', model.value));
    }
    function reset() {
      if (draft.value !== model.value) {
        draft.value = model.value;
      }
      activated.value = false;
      nextTick(() => context.emit('reset', model.value));
    }
    function focus() {
      var _a;
      (_a = control.value) === null || _a === void 0 ? void 0 : _a.focus();
    }
    function blur() {
      var _a;
      (_a = control.value) === null || _a === void 0 ? void 0 : _a.blur();
    }
    return {
      attributes,
      root,
      rootStyles,
      control,
      controlStyles,
      actionStyles,
      model,
      draft,
      activated,
      length,
      maxLength,
      onFocus,
      onBlur,
      onTextClick,
      apply,
      reset,
      focus,
      blur
    };
  }
});

const _hoisted_1 = {
  class: /*#__PURE__*/normalizeClass(['layout-row', 'gap-sm'])
};
const _hoisted_2 = ["type", "placeholder", "activated"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrText = resolveComponent("OrText");
  const _component_OrInlineInputBox = resolveComponent("OrInlineInputBox");
  const _component_OrIconButton = resolveComponent("OrIconButton");
  const _component_OrHint = resolveComponent("OrHint");
  const _component_OrError = resolveComponent("OrError");
  return openBlock(), createElementBlock("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles)
  }, [createElementVNode("div", _hoisted_1, [createVNode(_component_OrInlineInputBox, {
    size: _ctx.size,
    invalid: !!_ctx.error,
    activated: _ctx.activated
  }, {
    default: withCtx(() => [_ctx.activated || !_ctx.draft ? withDirectives((openBlock(), createElementBlock("input", mergeProps({
      key: 0,
      ref: 'control'
    }, _ctx.attributes, {
      "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => _ctx.draft = $event),
      class: _ctx.controlStyles,
      type: _ctx.type,
      placeholder: _ctx.placeholder,
      activated: _ctx.activated,
      onInput: _cache[1] || (_cache[1] = $event => _ctx.$emit('input', $event)),
      onKeydown: _cache[2] || (_cache[2] = $event => _ctx.$emit('keydown', $event)),
      onKeyup: _cache[3] || (_cache[3] = $event => _ctx.$emit('keyup', $event)),
      onFocus: _cache[4] || (_cache[4] = $event => {
        _ctx.$emit('focus', $event);
        _ctx.onFocus();
      }),
      onBlur: _cache[5] || (_cache[5] = $event => {
        _ctx.$emit('blur', $event);
        _ctx.onBlur();
      })
    }), null, 16 /* FULL_PROPS */, _hoisted_2)), [[vModelDynamic, _ctx.draft, void 0, {
      trim: true
    }]]) : (openBlock(), createBlock(_component_OrText, {
      key: 1,
      text: _ctx.draft.toString(),
      "slice-index": _ctx.sliceIndex,
      class: normalizeClass(_ctx.controlStyles),
      onClick: _ctx.onTextClick
    }, null, 8 /* PROPS */, ["text", "slice-index", "class", "onClick"]))]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["size", "invalid", "activated"]), _ctx.activated && !_ctx.saveOnBlur ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass(_ctx.actionStyles)
  }, [createVNode(_component_OrIconButton, {
    icon: 'done',
    color: 'inherit',
    onClick: withModifiers(_ctx.apply, ["stop"])
  }, null, 8 /* PROPS */, ["onClick"]), createVNode(_component_OrIconButton, {
    icon: 'close',
    color: 'inherit',
    onClick: withModifiers(_ctx.reset, ["stop"])
  }, null, 8 /* PROPS */, ["onClick"])], 2 /* CLASS */)) : createCommentVNode("v-if", true)]), _ctx.$slots.hint || _ctx.hint || _ctx.maxLength ? withDirectives((openBlock(), createBlock(_component_OrHint, {
    key: 0
  }, createSlots({
    default: withCtx(() => [renderSlot(_ctx.$slots, "hint", {}, () => [createTextVNode(toDisplayString(_ctx.hint), 1 /* TEXT */)])]),
    _: 2 /* DYNAMIC */
  }, [_ctx.maxLength ? {
    name: "addon",
    fn: withCtx(() => [createTextVNode(toDisplayString(_ctx.length) + " / " + toDisplayString(_ctx.maxLength), 1 /* TEXT */)]),
    key: "0"
  } : undefined]), 1536 /* NEED_PATCH, DYNAMIC_SLOTS */)), [[vShow, !_ctx.error]]) : createCommentVNode("v-if", true), withDirectives(createVNode(_component_OrError, null, createSlots({
    default: withCtx(() => [createTextVNode(toDisplayString(_ctx.error) + " ", 1 /* TEXT */)]),
    _: 2 /* DYNAMIC */
  }, [_ctx.maxLength ? {
    name: "addon",
    fn: withCtx(() => [createTextVNode(toDisplayString(_ctx.length) + " / " + toDisplayString(_ctx.maxLength), 1 /* TEXT */)]),
    key: "0"
  } : undefined]), 1536 /* NEED_PATCH, DYNAMIC_SLOTS */), [[vShow, typeof _ctx.error === 'string' && _ctx.error.length > 0]])], 2 /* CLASS */);
}

var css_248z = "\ninput[data-v-29d9ad19] {\n  min-width: 0;\n  background: none;\n  outline: none;\n}\ninput[type=\"number\"][data-v-29d9ad19]::-webkit-inner-spin-button {\n  display: none;\n}\ninput[type=\"search\"][data-v-29d9ad19]::-webkit-search-cancel-button {\n  display: none;\n}\n";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-29d9ad19";
script.__file = "src/components/or-inline-input-v3/OrInlineInput.vue";

export { InlineInputType as I, script as a, styleInject as s };
